import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Divider,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  LocalPrintshop as PrintIcon,
  ReceiptLong as ReceiptIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Paid as PaidIcon,
  AttachMoney as AmountIcon,
  Assignment as TicketIcon,
} from "@mui/icons-material";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";

type Payment = { method: string; amount: number; date: string; receiptNo?: string };
type Client = { fullName: string; email?: string; phone?: string; address?: string };
type Shop = {
  tenantName?: string;
  tenantAddress?: string;
  tenantPhone?: string;
  tenantEmail?: string;
  tenantTaxId?: string;
  logoURL?: string;
};
type TicketSummary = {
  _id?: string;
  ticketNumber?: string;
  status?: string;
  jobType?: string;
  deviceType?: string;
  brand?: string;
  model?: string;
  serialOrImei?: string;
  clientName?: string;
};
type Invoice = {
  _id: string;
  invoiceNumber: string | number;
  createdAt: string;
  paymentStatus: string;
  items: Array<{
    type?: string;
    name: string;
    description?: string;
    quantity: number;
    sellingPrice: number;
    taxRate?: number;
    discount?: number;
    lineTotal?: number;
  }>;
  subtotal: number;
  taxTotal: number;
  discountTotal: number;
  grandTotal: number;
  payments: Payment[];
  notes?: string;
  clientId: Client | string;
  ticketId?: TicketSummary | string | null;
  createdBy?: { fullName?: string };
};

export default function InvoiceDetailPage() {
  useProtectedRoute();
  const { token, role } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const printRef = useRef<HTMLDivElement>(null);

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [shop, setShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [sendMethod, setSendMethod] = useState<"whatsapp" | "email">("whatsapp");
  const [sendLoading, setSendLoading] = useState(false);
  const [reversing, setReversing] = useState(false);

  const surfaceBorder = `1px solid ${theme.palette.mode === "light" ? "#e5e7eb" : theme.palette.divider}`;
  const heroGradient = useMemo(
    () =>
      `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 55%, #0f172a 100%)`,
    [theme]
  );

  useEffect(() => {
    if (!id || !token) return;
    void fetchInvoice();
    void fetchShop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoice(res.data);
    } catch {
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchShop = async () => {
    try {
      const res = await api.get(`/shops/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShop(res.data);
    } catch {
      setShop(null);
    }
  };

  const client = (typeof invoice?.clientId === "object" ? (invoice?.clientId as Client) : undefined) || undefined;
  const ticket =
    invoice?.ticketId && typeof invoice.ticketId === "object"
      ? (invoice.ticketId as TicketSummary)
      : undefined;
  const linkedTicketId =
    (typeof invoice?.ticketId === "string" ? invoice.ticketId : undefined) || ticket?._id || undefined;
  const linkedTicketLabel = ticket?.ticketNumber ? `#${ticket.ticketNumber}` : linkedTicketId ? `Ticket ${linkedTicketId}` : "";
  const linkedTicketClientName = ticket?.clientName || client?.fullName;
  const linkedTicketDevice = [ticket?.deviceType, ticket?.brand, ticket?.model].filter(Boolean).join(" | ");

  const totalPaid = invoice?.payments?.reduce((sum, p) => sum + (Number(p.amount) || 0), 0) || 0;
  const outstanding = Math.max(0, (invoice?.grandTotal || 0) - totalPaid);

  const fmtMoney = (n?: number) =>
    typeof n === "number" ? Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(n) : "-";
  const fmtDateTime = (iso?: string) => (iso ? new Date(iso).toLocaleString() : "-");

  const chipColor = (status: string): "success" | "warning" | "error" | "default" | "info" => {
    const s = (status || "").toUpperCase();
    if (s === "PAID") return "success";
    if (s === "PARTIALLY PAID" || s === "PARTIAL") return "warning";
    if (s === "UNPAID") return "error";
    if (s === "DRAFT") return "default";
    return "info";
  };

  const ticketStatusColor = (status?: string) => {
    const s = (status || "").toLowerCase();
    if (s === "open" || s === "in-progress") return "#10b981";
    if (s === "pending" || s === "on-hold") return "#f59e0b";
    if (s === "completed" || s === "closed") return "#3b82f6";
    return "#6b7280";
  };

  const handlePrint = () => {
    if (!printRef.current) return;
    const content = printRef.current.innerHTML;
    const original = document.body.innerHTML;
    document.body.innerHTML = content;
    window.print();
    document.body.innerHTML = original;
    window.location.reload();
  };

  const handleAddPayment = async () => {
    if (!invoice) return;
    const amt = parseFloat(paymentAmount);
    if (!amt || amt <= 0) {
      alert("Enter a valid amount");
      return;
    }
    try {
      await api.post(
        `/invoices/${invoice._id}/payment`,
        { amount: amt, method: paymentMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPaymentAmount("");
      await fetchInvoice();
    } catch (err: any) {
      alert("Failed to add payment: " + (err?.response?.data?.message || ""));
    }
  };

  const handleSend = async () => {
    if (!invoice) return;
    setSendLoading(true);
    try {
      await api.post(
        `/invoices/${invoice._id}/send`,
        { method: sendMethod },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Invoice sent via ${sendMethod}`);
    } catch (err: any) {
      alert("Failed to send invoice: " + (err?.response?.data?.message || ""));
    }
    setSendLoading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${invoice?._id}`, { headers: { Authorization: `Bearer ${token}` } });
      navigate("/invoices");
    } catch {
      alert("Delete failed.");
    }
  };

  const handleReverseDelete = async () => {
    if (!invoice) return;
    if (invoice.payments && invoice.payments.length > 0) {
      alert("This invoice has payments and cannot be reversed.");
      return;
    }
    const ok = window.confirm(
      "Reverse & delete this invoice?\n\nThis restores inventory for parts/accessories and unmarks the related ticket as invoiced."
    );
    if (!ok) return;

    setReversing(true);
    try {
      await api.post(`/invoices/${invoice._id}/reverse-delete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert("Invoice reversed and deleted.");
      navigate("/invoices");
    } catch (err: any) {
      alert("Reverse delete failed: " + (err?.response?.data?.message || ""));
    } finally {
      setReversing(false);
    }
  };

  const openLinkedTicket = () => {
    if (linkedTicketId) {
      navigate(`/tickets/${linkedTicketId}`);
    }
  };

  if (loading)
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );

  if (!invoice)
    return (
      <Box p={4} textAlign="center">
        <Typography color="error">Invoice not found.</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/invoices")} startIcon={<ArrowBackIcon />}>
          Back to Invoices
        </Button>
      </Box>
    );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: { xs: 1, md: 2 } }}>
      {/* HERO */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: surfaceBorder,
          background: heroGradient,
          color: "white",
        }}
      >
        <CardContent sx={{ py: { xs: 2, md: 2.5 } }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.15)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <ReceiptIcon />
                </Box>
                <Box>
                  <Typography variant={isSmDown ? "h5" : "h4"} sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                    Invoice #{invoice.invoiceNumber}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Created {fmtDateTime(invoice.createdAt)}
                  </Typography>
                  {linkedTicketId && (
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Linked Ticket: {linkedTicketLabel}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md="auto">
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                <Chip
                  label={invoice.paymentStatus}
                  color={chipColor(invoice.paymentStatus)}
                  sx={{ fontWeight: 800, bgcolor: "white", color: "inherit" }}
                />
                {linkedTicketId && (
                  <Button
                    variant="outlined"
                    startIcon={<TicketIcon />}
                    onClick={openLinkedTicket}
                    sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)" }}
                  >
                    View Ticket
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate("/invoices")}
                  sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)" }}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  sx={{ fontWeight: 800 }}
                >
                  Print
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* BODY */}
      <Grid container spacing={3}>
        {/* LEFT: Invoice content (print area) */}
        <Grid item xs={12} lg={8}>
          <Card elevation={0} sx={{ borderRadius: 3, border: surfaceBorder }} ref={printRef}>
            <CardHeader
              title={<Typography variant="h6" fontWeight={900}>Invoice</Typography>}
              subheader={invoice.createdBy?.fullName ? `Created by ${invoice.createdBy.fullName}` : undefined}
            />
            <Divider />

            {/* Shop + Bill To */}
            <CardContent sx={{ pt: 2 }}>
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    From
                  </Typography>
                  {shop?.logoURL && (
                    <Box sx={{ mb: 1 }}>
                      <img src={shop.logoURL} alt="Logo" style={{ height: 36, objectFit: "contain" }} />
                    </Box>
                  )}
                  <Typography fontWeight={700}>{shop?.tenantName || "Your Business"}</Typography>
                  {shop?.tenantAddress && <Typography variant="body2">{shop.tenantAddress}</Typography>}
                  {shop?.tenantPhone && <Typography variant="body2">Phone: {shop.tenantPhone}</Typography>}
                  {shop?.tenantEmail && <Typography variant="body2">Email: {shop.tenantEmail}</Typography>}
                  {shop?.tenantTaxId && (
                    <Typography variant="caption" color="text.secondary">
                      Tax ID: {shop.tenantTaxId}
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Bill To
                  </Typography>
                  <Typography fontWeight={700}>{client?.fullName || "-"}</Typography>
                  {client?.address && <Typography variant="body2">{client.address}</Typography>}
                  {client?.email && <Typography variant="body2">{client.email}</Typography>}
                  {client?.phone && <Typography variant="body2">{client.phone}</Typography>}
                </Grid>
              </Grid>
            </CardContent>

            {/* Items */}
            <Divider />
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                Items
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: surfaceBorder, borderRadius: 2, overflow: "hidden" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ "& th": { backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover" } }}>
                      <TableCell><strong>Item</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Qty</strong></TableCell>
                      <TableCell><strong>Price</strong></TableCell>
                      <TableCell><strong>Tax</strong></TableCell>
                      <TableCell><strong>Discount</strong></TableCell>
                      <TableCell align="right"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.items.map((item, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {item.type && (
                              <Chip size="small" label={item.type} variant="outlined" />
                            )}
                            <Typography fontWeight={700}>{item.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{item.description || <Typography color="text.disabled">-</Typography>}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{fmtMoney(item.sellingPrice)}</TableCell>
                        <TableCell>{Number(item.taxRate || 0)}%</TableCell>
                        <TableCell>{fmtMoney(Number(item.discount || 0))}</TableCell>
                        <TableCell align="right">{fmtMoney(Number(item.lineTotal || 0))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals */}
              <Box sx={{ mt: 2, display: "grid", gap: 0.5, justifyContent: "end" }}>
                <Typography>Subtotal: {fmtMoney(invoice.subtotal)}</Typography>
                <Typography>Tax: {fmtMoney(invoice.taxTotal)}</Typography>
                <Typography>Discount: -{fmtMoney(invoice.discountTotal)}</Typography>
                <Typography variant="h6" fontWeight={900}>
                  Total: {fmtMoney(invoice.grandTotal)}
                </Typography>
                <Typography color="success.main">Paid: {fmtMoney(totalPaid)}</Typography>
                <Typography color="error.main">Outstanding: {fmtMoney(outstanding)}</Typography>
              </Box>

              {/* Notes */}
              {invoice.notes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{invoice.notes}</Typography>
                </Box>
              )}
            </CardContent>

            {/* Payment History (still inside print area) */}
            <Divider />
            <CardContent>
              <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                Payment History
              </Typography>
              {invoice.payments?.length ? (
                <TableContainer component={Paper} elevation={0} sx={{ border: surfaceBorder, borderRadius: 2, overflow: "hidden" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ "& th": { backgroundColor: theme.palette.mode === "light" ? "#f8fafc" : "action.hover" } }}>
                        <TableCell><strong>Receipt #</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Method</strong></TableCell>
                        <TableCell align="right"><strong>Amount</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.payments.map((p, i) => (
                        <TableRow key={i}>
                          <TableCell>{p.receiptNo || `${invoice.invoiceNumber}-${i + 1}`}</TableCell>
                          <TableCell>{fmtDateTime(p.date)}</TableCell>
                          <TableCell>{p.method}</TableCell>
                          <TableCell align="right">{fmtMoney(Number(p.amount))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="text.secondary">No payments recorded yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT: Linked ticket & actions */}
        <Grid item xs={12} lg={4}>
          {linkedTicketId && (
            <Card elevation={0} sx={{ borderRadius: 3, border: surfaceBorder, mb: 3 }}>
              <CardHeader
                avatar={
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    <TicketIcon fontSize="small" />
                  </Box>
                }
                title={<Typography fontWeight={900}>Linked Ticket</Typography>}
                subheader={linkedTicketLabel || "Ticket"}
                action={
                  ticket?.status ? (
                    <Chip
                      size="small"
                      label={ticket.status}
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 700,
                        bgcolor: ticketStatusColor(ticket.status),
                        color: "#fff",
                      }}
                    />
                  ) : undefined
                }
              />
              <Divider />
              <CardContent sx={{ display: "grid", gap: 1 }}>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 700 }}>Client:</Box> {linkedTicketClientName || "-"}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 700 }}>Job Type:</Box> {ticket?.jobType || "-"}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 700 }}>Device:</Box> {linkedTicketDevice || "-"}
                </Typography>
                <Typography variant="body2">
                  <Box component="span" sx={{ fontWeight: 700 }}>Identifiers:</Box> {ticket?.serialOrImei || "-"}
                </Typography>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button variant="contained" fullWidth startIcon={<TicketIcon />} onClick={openLinkedTicket}>
                  Open Ticket
                </Button>
              </CardActions>
            </Card>
          )}

          {/* Quick actions */}
          <Card elevation={0} sx={{ borderRadius: 3, border: surfaceBorder, mb: 3 }}>
            <CardHeader title={<Typography fontWeight={900}>Actions</Typography>} />
            <Divider />
            <CardActions sx={{ p: 2, pt: 1.5, gap: 1, flexWrap: "wrap" }}>
              {(["ADMIN", "MANAGER", "SUPERADMIN"] as string[]).includes(role || "") && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<RestoreIcon />}
                    onClick={handleReverseDelete}
                    disabled={(invoice.payments?.length ?? 0) > 0 || reversing}
                    title={(invoice.payments?.length ?? 0) > 0 ? "Cannot reverse an invoice that has payments." : ""}
                  >
                    {reversing ? "Reversing..." : "Reverse & Delete"}
                  </Button>
                </>
              )}
            </CardActions>
          </Card>

          {/* Add Payment */}
          <Card elevation={0} sx={{ borderRadius: 3, border: surfaceBorder, mb: 3 }}>
            <CardHeader
              title={<Typography fontWeight={900}>Add Payment</Typography>}
              subheader={<Typography variant="caption" color="text.secondary">Outstanding: {fmtMoney(outstanding)}</Typography>}
            />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Amount"
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <AmountIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as string)} fullWidth>
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Card">Card</MenuItem>
                    <MenuItem value="Mobile Money">Mobile Money</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions sx={{ p: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<PaidIcon />}
                onClick={handleAddPayment}
                disabled={!paymentAmount}
              >
                Record Payment
              </Button>
            </CardActions>
          </Card>

          {/* Send Invoice */}
          <Card elevation={0} sx={{ borderRadius: 3, border: surfaceBorder }}>
            <CardHeader title={<Typography fontWeight={900}>Send Invoice</Typography>} />
            <Divider />
            <CardContent>
              <Select value={sendMethod} onChange={(e) => setSendMethod(e.target.value as any)} fullWidth>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="email">Email</MenuItem>
              </Select>
            </CardContent>
            <CardActions sx={{ p: 2 }}>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSend}
                disabled={sendLoading}
                fullWidth
              >
                {sendLoading ? "Sending..." : "Send"}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Space before fixed footer seam */}
      <Box sx={{ height: 12 }} />
    </Box>
  );
}
